import {
  discountedExclVat,
  type SaleEnginePricedLine
} from "~/lib/sale-engine";
import { roundAwayFromZero } from "~/utils/round-away-from-zero";

import { type KioskLineMeta } from "./prepare-kiosk-basket-line";
import { type KioskDisplayBasketItem } from "./types";

/**
 * Project a priced engine line back to a kiosk basket item. Pure, mirrors POS
 * `projectPosLine`. Returns the line-total allowance deduction for the payload
 * (V0-native, like POS); the v4 transport divides it back to per-unit.
 */
export function projectKioskLine(
  meta: KioskLineMeta,
  priced: SaleEnginePricedLine
): { item: KioskDisplayBasketItem; allowanceLineTotal: number } {
  const before = priced.lineTotalBeforeDiscount;
  const afterPerLine = before - priced.perLineDiscount;
  const preAllowanceGross = roundAwayFromZero(afterPerLine);

  const allowanceLineTotal = priced.allowanceDeduction;

  const unitTotalPriceInclVat = preAllowanceGross - allowanceLineTotal;

  // Strikethrough basis: allowance has no engine listPrice → pre-allowance gross.
  let unitTotalListPriceInclVat = priced.unitListPrice;
  if (unitTotalListPriceInclVat === null && allowanceLineTotal > 0) {
    unitTotalListPriceInclVat = preAllowanceGross;
  }

  // PWEB-132: excl tracks the discounted unitTotalPriceInclVat (what's charged).
  const unitTotalPriceExclVat = discountedExclVat(
    meta.unitTotalPriceExclVatBase,
    unitTotalPriceInclVat,
    before
  );

  const unitTotalListPriceExclVat =
    unitTotalListPriceInclVat === null
      ? null
      : discountedExclVat(
          meta.unitTotalPriceExclVatBase,
          unitTotalListPriceInclVat,
          before
        );

  return {
    allowanceLineTotal,
    item: {
      basketItemId: meta.basketItemId,
      product: meta.displayProduct,
      quantity: meta.quantity,
      unitTotalPriceExclVat,
      unitTotalListPriceExclVat,
      unitPriceExclVat: meta.unitPriceExclVat,
      unitTotalPriceInclVat,
      unitTotalListPriceInclVat,
      vatRate: meta.vatRate,
      // Net of what an allowance covered. Zero today — the kiosk never asks
      // the engine to absorb a deposit — but reading the full fee instead
      // would overcharge the moment POS-3657 turns that on.
      depositTotalInclVat:
        priced.depositTotal - priced.depositAllowanceDeduction,
      depositTotalExclVat: meta.depositTotalExclVat,
      productDepositTotalInclVat: meta.productDepositTotalInclVat,
      productDepositTotalExclVat: meta.productDepositTotalExclVat,
      variantDepositTotals: meta.variantDepositTotals,
      allowanceDeductionInclVat: allowanceLineTotal,
      // Capped money deduction (> 0) ⇒ unitPrice; else keep prepare's value.
      appliedAllowanceType:
        allowanceLineTotal > 0 ? "unitPrice" : meta.appliedAllowanceType,
      isRedeemed: meta.isRedeemed
    }
  };
}
